import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import Heading from '@atlaskit/heading';

import { addFlag } from '../actions/flag-actions';
import {
  confirmedRecoveryCode,
  recoverAccount,
  resendOtp,
  sendRecoveryEmail,
  verifyOtpCode,
} from '../actions/multi-factor-actions';
import MultiFactorForm from '../components/MultiFactorForm';
import RecoveryEmailForm from '../components/RecoveryEmailForm';
import RecoveryEmailSent from '../components/RecoveryEmailSent';
import RecoveryForm from '../components/RecoveryForm';
import RecoveryPage from '../components/RecoveryPage';
import ButtonLink from '../components/ButtonLink';
import { PrivacyPolicyLink, UserNoticeLink } from '../components/links';
import WhiteboxPage from '../components/WhiteboxPage';
import Bull from '../components/BulletSeparator';
import messages from './MultiFactorPage.messages';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { MultiFactorMode, MultiFactorOTPError } from '../types';
import {
  useAnalyticsClient,
  useButtonClickedEvent,
  useLinkClickedEvent,
  useOperationalEvent,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { useNavigateToLoginPrompt } from '../utilities/hooks/history';
import { usePrevious } from '../utilities/hooks';
import VerifyEmailOtp, {
  VerifyEmailOtpErrorCode,
} from '../components/VerifyEmailOtp/VerifyEmailOtp';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import { usePageStateLoader, fetchPageState } from '../utilities/hooks/useGlobalPageState';
import { useExecutionTracker } from '../utilities/hooks/useExecutionTracker';
import {
  checkFeatureGate,
  checkFeatureGateOnceInitialized,
} from '../utilities/feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import MultiFactorSwitchMethods from './MultiFactorSwitchMethods';

export type MultiFactorPageProps = {
  isExpired: boolean;
};

const pageId = 'multiFactorPage';
const challengeId = '2svChallengeScreen';
const expireId = '2svRecoveryLinkExpiredScreen';
const emergencyRecoveryKey = '2svRecoveryKeyScreen';
const saveNewRecoveryKey = '2svSaveNewRecoveryKey';
const accountRecovery = '2svAccountRecoveryScreen';
const recoveryEmailSent = '2svRecoveryEmailSentScreen';

export async function mfaLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/login/mfa/state', request, {
    capabilities: ['capability-group--login', 'capability-name--mfa'],
  });
}

export async function recoverExpiredLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/2step/recover/expired/state', request, {
    capabilities: ['capability-group--login', 'capability-name--mfa'],
  });
}

export const MultiFactorPage = ({ isExpired }: MultiFactorPageProps) => {
  const dispatch = useAppDispatch();
  const { isExecuting, executeWithTracking } = useExecutionTracker();

  const isEmbedded = useAppSelector(state => state.microbranding.isEmbedded);
  const {
    method: multifactorMethod,
    phoneNumber,
    isLoading,
    isManaged,
    isRecoveryEmailSent,
    recoveryEmailError,
    fieldErrors,
    recoveryCode,
    clearInputTrigger,
  } = useAppSelector(state => state.multiFactor);

  const email = useAppSelector(state => {
    const mfEmail = state.multiFactor.email;
    const userEmail = state.user.email;
    return mfEmail && mfEmail.length > 0 ? mfEmail : userEmail;
  });

  const { search } = useLocation();
  const navigate = useNavigateSoftOrHard();
  const { formatMessage } = useIntl();
  const analyticsClient = useAnalyticsClient();

  usePageViewedEvent(pageId);
  useOperationalEvent({
    page: pageId,
    action: `loadMultiFactorPageSuccessful`,
    subject: 'sli',
  });

  const isInitialRender = useRef(true);

  const mfaEventAttributes = useMemo(() => ({ multifactorMethod }), [multifactorMethod]);
  // User will initially be prompted for the challenged factor, but can switch to the "Recovery code" screen,
  // or futher from there, the "Account recovery" screen.
  // This mode is to keep track of which screen the user is on.
  const [mode, setMode] = useState<MultiFactorMode>('CHALLENGED_FACTOR');
  const previousMode = usePrevious(mode);

  useEffect(() => {
    // After initial render, whenever the mode changes we want to trigger pageViewedEvents:
    if (isInitialRender.current) {
      return;
    }

    isInitialRender.current = false;

    if (mode !== previousMode) {
      switch (mode) {
        case 'RECOVERY_EMAIL':
          analyticsClient.pageViewedEvent(accountRecovery);
          break;
        case 'RECOVERY_CODE':
          analyticsClient.pageViewedEvent(emergencyRecoveryKey, mfaEventAttributes);
          break;
        case 'CHALLENGED_FACTOR':
          analyticsClient.pageViewedEvent(challengeId, mfaEventAttributes);
          break;
      }
    }
  }, [mode, analyticsClient, multifactorMethod, mfaEventAttributes, previousMode]);

  const trackConfirmedRecoveryCode = useButtonClickedEvent(
    saveNewRecoveryKey,
    '2svSavedNewRecoveryKey'
  );

  const dispatchConfirmedRecoveryCode = useCallback(() => {
    trackConfirmedRecoveryCode();
    dispatch(confirmedRecoveryCode(search, analyticsClient, navigate));
  }, [trackConfirmedRecoveryCode, dispatch, search, analyticsClient, navigate]);

  const trackVerifyOtpCode = useButtonClickedEvent(
    challengeId,
    '2svChallengeLogin',
    mfaEventAttributes
  );

  const dispatchVerifyOtpCode = useCallback(
    (otpCode: string) => {
      trackVerifyOtpCode();
      dispatch(verifyOtpCode(search, otpCode, analyticsClient, navigate));
    },
    [trackVerifyOtpCode, dispatch, search, analyticsClient, navigate]
  );

  const trackVerifyRecoveryCode = useButtonClickedEvent(
    emergencyRecoveryKey,
    '2svRecoveryKeyLogin',
    mfaEventAttributes
  );

  const verifyRecoveryCode = useCallback(
    (recoveryCode: string) => {
      trackVerifyRecoveryCode();
      dispatch(recoverAccount(search, recoveryCode, analyticsClient, navigate));
    },
    [trackVerifyRecoveryCode, dispatch, search, analyticsClient, navigate]
  );

  const trackResendSms = useButtonClickedEvent(challengeId, '2svResendSms');

  const resendOtpSms = useCallback(() => {
    trackResendSms();
    dispatch(resendOtp(search, analyticsClient, navigate, multifactorMethod));
  }, [trackResendSms, dispatch, search, analyticsClient, navigate, multifactorMethod]);

  const trackResendEmailOtp = useButtonClickedEvent(challengeId, '2svResendEmailOtp');

  const resendOtpEmail = useCallback(() => {
    trackResendEmailOtp();
    dispatch(resendOtp(search, analyticsClient, navigate, multifactorMethod));
  }, [trackResendEmailOtp, dispatch, search, analyticsClient, navigate, multifactorMethod]);

  const trackUseOtpCode = useButtonClickedEvent(
    emergencyRecoveryKey,
    'usePhone',
    mfaEventAttributes
  );

  const trackUseSecurityKey = useButtonClickedEvent(
    emergencyRecoveryKey,
    'useSecurityKey',
    mfaEventAttributes
  );

  const useOtpCode = useCallback(() => {
    trackUseOtpCode();
    setMode('CHALLENGED_FACTOR');
  }, [trackUseOtpCode]);

  const useSecurityKey = useCallback(() => {
    trackUseSecurityKey();
    setMode('CHALLENGED_FACTOR');
  }, [trackUseSecurityKey]);

  const trackUseRecoveryCode = useButtonClickedEvent(accountRecovery, '2svAccountRecoveryScreen');

  const trackCantUsePhone = useButtonClickedEvent(challengeId, 'cantUsePhone');

  const trackCantUseSecurityKey = useButtonClickedEvent(challengeId, 'cantUseSecurityKey');

  const useRecoveryCode = useCallback(() => {
    switch (mode) {
      case 'RECOVERY_EMAIL':
        trackUseRecoveryCode();
        break;
      case 'CHALLENGED_FACTOR':
        if (multifactorMethod === 'security-key') {
          trackCantUseSecurityKey();
        } else {
          trackCantUsePhone();
        }
        break;
    }
    setMode('RECOVERY_CODE');
  }, [trackUseRecoveryCode, trackCantUsePhone, trackCantUseSecurityKey, mode, multifactorMethod]);

  const trackUseRecoveryEmail = useButtonClickedEvent(emergencyRecoveryKey, 'noRecoveryKey');

  const useRecoveryEmail = useCallback(() => {
    trackUseRecoveryEmail();
    setMode('RECOVERY_EMAIL');
  }, [trackUseRecoveryEmail]);

  const navigateToLogin = useNavigateToLoginPrompt();

  const trackReturnToLogin = useLinkClickedEvent(pageId, 'returnToLogin');

  const handleReturnToLogin = useCallback(() => {
    trackReturnToLogin();
    navigateToLogin();
  }, [trackReturnToLogin, navigateToLogin]);

  const useNewRecoveryKeyLabel = checkFeatureGate(
    AidFrontendFeatureGate.USE_NEW_LABEL_FOR_USE_RECOVERY_KEY_BUTTON
  );

  const renderMainContentFooter = () => {
    if (mode === 'RECOVERY_EMAIL' && !isRecoveryEmailSent && !isExpired) {
      return (
        <ButtonLink id="two-step-verification-enter-back-recovery-key" onClick={useRecoveryCode}>
          <FormattedMessage
            id="mf.backtouserecoverycode"
            defaultMessage="Enter your recovery key"
          />
        </ButtonLink>
      );
    } else if (isRecoveryEmailSent || isExpired) {
      return (
        <ButtonLink id="two-step-verification-switch-challenge-type" onClick={resendEmail}>
          <FormattedMessage id="mf.resendrecoveryemail" defaultMessage="Resend recovery email" />
        </ButtonLink>
      );
    } else if (mode === 'CHALLENGED_FACTOR') {
      if (multifactorMethod === 'security-key') {
        return (
          <div>
            <MultiFactorSwitchMethods
              screenId={challengeId}
              isSwitching={isExecuting}
              switchWithTracking={executeWithTracking}
            />
            <ButtonLink
              id="two-step-verification-switch-challenge-type-1"
              testId="two-step-verification-switch-challenge-type-idf-testid"
              onClick={useRecoveryCode}
              isDisabled={isExecuting}>
              {useNewRecoveryKeyLabel ? (
                <FormattedMessage {...messages.mfSwitchToRecoveryCode} />
              ) : (
                <FormattedMessage
                  id="mf.cantuseyoursecuritykey"
                  defaultMessage="Can't use your security key?"
                />
              )}
            </ButtonLink>
          </div>
        );
      } else if (multifactorMethod === 'sms' || multifactorMethod === 'otp') {
        return (
          <div>
            <MultiFactorSwitchMethods
              screenId={challengeId}
              isSwitching={isExecuting}
              switchWithTracking={executeWithTracking}
            />
            <ButtonLink
              id="two-step-verification-switch-challenge-type"
              testId="two-step-verification-switch-challenge-type-idf-testid"
              onClick={useRecoveryCode}
              isDisabled={isExecuting}>
              {useNewRecoveryKeyLabel ? (
                <FormattedMessage {...messages.mfSwitchToRecoveryCode} />
              ) : (
                <FormattedMessage id="mf.userecoverycode" defaultMessage="Can't use your phone?" />
              )}
            </ButtonLink>
          </div>
        );
      } else {
        // email-otp should never call this function
        return null;
      }
    } else if (mode === 'RECOVERY_CODE') {
      return (
        <div>
          {!isManaged && (
            <div>
              <ButtonLink
                id="two-step-verification-switch-challenge-type"
                onClick={useRecoveryEmail}>
                <FormattedMessage
                  id="mf.userecoveryemail"
                  defaultMessage="Can't find your recovery key?"
                />
              </ButtonLink>
            </div>
          )}
          {multifactorMethod === 'security-key' ? (
            <div>
              <ButtonLink onClick={useSecurityKey}>
                <FormattedMessage
                  id="mf.useyoursecuritykey"
                  defaultMessage="Use your security key"
                />
              </ButtonLink>
            </div>
          ) : (
            <div>
              <ButtonLink onClick={useOtpCode}>
                <FormattedMessage id="mf.backtouseotpcode" defaultMessage="Use your phone" />
              </ButtonLink>
            </div>
          )}
        </div>
      );
    } else if (!isEmbedded) {
      return (
        <a
          href="https://confluence.atlassian.com/cloud/secure-your-account-with-two-step-verification-939505063.html#Secureyouraccountwithtwo-stepverification-Recoveryouraccount"
          target="_blank"
          rel="noopener noreferrer">
          <FormattedMessage id="mf.gethelp" defaultMessage="Help for two-step verification" />
        </a>
      );
    }
  };

  const renderFooter = () => {
    if (!isEmbedded) {
      return (
        <ul className="fine-print">
          <li>
            <PrivacyPolicyLink isMobileLink={false} />
          </li>
          <Bull />
          <li>
            <UserNoticeLink isMobileLink={false} />
          </li>
        </ul>
      );
    }
  };

  const renderHeader = () => {
    if (isExpired) {
      return (
        <Heading size="small" as="h1">
          <FormattedMessage
            id="login.title.recoverylinkexpired"
            defaultMessage="Your recovery link expired"
          />
        </Heading>
      );
    } else if (mode === 'CHALLENGED_FACTOR' || mode === 'RECOVERY_CODE') {
      return (
        <Heading size="small" as="h1">
          <FormattedMessage
            id="continue.with.two.step.verification"
            defaultMessage="Continue with two-step verification"
          />
        </Heading>
      );
    } else {
      return (
        <Heading size="small" as="h1">
          <FormattedMessage id="mf.title.norecoverycode" defaultMessage="Recover your account" />
        </Heading>
      );
    }
  };

  const trackSendRecoveryEmail = useButtonClickedEvent(accountRecovery, 'sendRecoveryEmail');
  const sendEmail = useCallback(() => {
    trackSendRecoveryEmail();
    dispatch(sendRecoveryEmail(email));
  }, [trackSendRecoveryEmail, dispatch, email]);

  const trackResendEmail = useButtonClickedEvent(recoveryEmailSent, 'resendEmail');
  const trackResendEmailExpired = useButtonClickedEvent(expireId, 'resendEmail');
  const resendEmail = useCallback(() => {
    sendEmail();
    if (isExpired) {
      trackResendEmailExpired();
    } else {
      trackResendEmail();
    }
    if (isRecoveryEmailSent) {
      dispatch(
        addFlag(
          'success',
          messages.mfRecoveryEmailResentSuccessTitle,
          messages.mfRecoveryEmailResentSuccessDesc
        )
      );
    } else {
      dispatch(
        addFlag(
          'error',
          messages.mfRecoveryEmailResentErrorTitle,
          messages.mfRecoveryEmailResentErrorDesc
        )
      );
    }
  }, [
    sendEmail,
    isExpired,
    isRecoveryEmailSent,
    trackResendEmailExpired,
    trackResendEmail,
    dispatch,
  ]);

  const renderWhiteboxPageContent = () => {
    if (isRecoveryEmailSent || isExpired) {
      return (
        <RecoveryEmailSent
          email={email}
          isExpired={isExpired}
          expirePageId={expireId}
          emailSentPageId={recoveryEmailSent}
        />
      );
    }

    if (mode === 'CHALLENGED_FACTOR') {
      if (multifactorMethod === 'sms' || multifactorMethod === 'otp') {
        return (
          <MultiFactorForm
            verifyOtpCode={dispatchVerifyOtpCode}
            resendOtp={resendOtpSms}
            isLoading={isLoading}
            otpError={fieldErrors.otpCode}
            multifactorMethod={multifactorMethod}
            phoneNumber={phoneNumber}
            clearInputTrigger={clearInputTrigger}
          />
        );
      }
      if (multifactorMethod === 'security-key') {
        return (
          <MultiFactorForm
            isLoading={isLoading}
            multifactorMethod={multifactorMethod}
            clearInputTrigger={clearInputTrigger}
          />
        );
      }
    } else if (mode === 'RECOVERY_CODE') {
      return (
        <RecoveryForm
          recoverAccount={verifyRecoveryCode}
          isLoading={isLoading}
          multifactorMethod={multifactorMethod}
          recoveryError={fieldErrors.recoveryCode}
        />
      );
    } else if (mode === 'RECOVERY_EMAIL') {
      return (
        <RecoveryEmailForm
          email={email}
          sendEmail={sendEmail}
          recoveryEmailError={recoveryEmailError}
        />
      );
    }
  };

  const renderGenericExperience = () => {
    return !recoveryCode ? (
      <WhiteboxPage
        pageId={pageId}
        header={renderHeader()}
        whiteboxFooter={renderMainContentFooter()}
        footer={renderFooter()}
        noCobranding>
        <Helmet>
          <title>{formatMessage(messages.loginHeadTitle)}</title>
        </Helmet>
        {renderWhiteboxPageContent()}
      </WhiteboxPage>
    ) : (
      <RecoveryPage
        recoveryCode={recoveryCode}
        onComplete={dispatchConfirmedRecoveryCode}
        isEmbedded={isEmbedded}
      />
    );
  };

  const verifyEmailOtpErrorCodeMapping: Record<
    MultiFactorOTPError['errorCode'],
    VerifyEmailOtpErrorCode
  > = {
    invalid_otp: 'invalid_otp',
    invalid_token: 'invalid_token',
    invalid_otp_format: 'invalid_otp_length',
  };

  const renderEmailOtpExperience = () => {
    const otpErrorCode = fieldErrors?.otpCode?.errorCode;
    const verifyEmailOtpErrorCode = !otpErrorCode
      ? undefined
      : verifyEmailOtpErrorCodeMapping[otpErrorCode] || 'unexpected_error';

    return (
      <VerifyEmailOtp
        pageId={pageId}
        flow="login-challenge"
        email={email}
        isLoading={isLoading}
        errorCode={verifyEmailOtpErrorCode}
        onVerifyEmailOtp={dispatchVerifyOtpCode}
        onResendEmailOtp={resendOtpEmail}
        additionalWhiteboxFooter={
          <ButtonLink id="two-step-verification-return-to-login" onClick={handleReturnToLogin}>
            <FormattedMessage
              id="mf.emailotp.return.to.login"
              defaultMessage="Log in to a different account"
            />
          </ButtonLink>
        }
      />
    );
  };

  usePageStateLoader();

  if (multifactorMethod === 'email-otp') {
    return renderEmailOtpExperience();
  } else {
    return renderGenericExperience();
  }
};

export default MultiFactorPage;
